import { computed, onMounted, ref, watch } from "vue";
import { useMetal } from "@/composables/useMetal";

type NisabType = "silver" | "gold";

const STORAGE_KEY = "zakat_calculator_state_v1";

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-PK").format(value);
}

function toRomanWords(value: number | null): string {
  if (!value || value <= 0) return "";

  if (value >= 100000) {
    const lakh = value / 100000;
    const lakhDisplay = Number.isInteger(lakh) ? String(lakh) : lakh.toFixed(1);
    return `${lakhDisplay} lakh`;
  }

  if (value >= 1000) {
    const hazar = value / 1000;
    const hazarDisplay = Number.isInteger(hazar) ? String(hazar) : hazar.toFixed(1);
    return `${hazarDisplay} hazar`;
  }

  return String(value);
}

export function useZakat() {
  const amount = ref<number | null>(null);
  const { metalRates } = useMetal();
  const selectedNisab = ref<NisabType>(metalRates.value.defaultNisab);
  const goldTola = ref(0);
  const silverTola = ref(0);

  const formattedAmount = computed(() =>
    amount.value && amount.value > 0 ? formatNumber(amount.value) : "",
  );
  const amountInWords = computed(() => toRomanWords(amount.value));
  const goldValue = computed(() =>
    Math.round(goldTola.value * (metalRates.value?.goldPerTola ?? 0)),
  );
  const silverValue = computed(() =>
    Math.round(silverTola.value * (metalRates.value?.silverPerTola ?? 0)),
  );
  const totalAmount = computed(() => (amount.value ?? 0) + goldValue.value + silverValue.value);
  const effectiveAmount = computed(() => (totalAmount.value > 0 ? totalAmount.value : null));
  const goldNisabFromRate = computed(() =>
    Math.round((metalRates.value?.goldPerTola ?? 0) * 7.5),
  );
  const silverNisabFromRate = computed(() =>
    Math.round((metalRates.value?.silverPerTola ?? 0) * 52.5),
  );
  const currentNisab = computed(() =>
    selectedNisab.value === "silver"
      ? silverNisabFromRate.value
      : goldNisabFromRate.value,
  );
  const isEligible = computed(
    () => totalAmount.value > 0 && totalAmount.value >= currentNisab.value,
  );
  const zakatAmount = computed(() =>
    isEligible.value ? totalAmount.value * 0.025 : 0,
  );
  const formattedCurrentNisab = computed(() => formatNumber(currentNisab.value));
  const formattedZakat = computed(() => formatNumber(Math.round(zakatAmount.value)));

  function setAmount(value: number | null) {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
      amount.value = null;
      return;
    }
    amount.value = Math.floor(value);
  }

  function increment(value: number) {
    const base = amount.value ?? 0;
    setAmount(base + Math.max(0, Math.floor(value)));
  }

  function decrement(value: number) {
    const base = amount.value ?? 0;
    const next = base - Math.max(0, Math.floor(value));
    setAmount(next > 0 ? next : null);
  }

  function toggleNisab() {
    selectedNisab.value = selectedNisab.value === "silver" ? "gold" : "silver";
  }

  function setGoldTola(value: number) {
    if (!Number.isFinite(value) || value < 0) {
      goldTola.value = 0;
      return;
    }
    goldTola.value = Math.round(value * 10) / 10;
  }

  function setSilverTola(value: number) {
    if (!Number.isFinite(value) || value < 0) {
      silverTola.value = 0;
      return;
    }
    silverTola.value = Math.round(value * 10) / 10;
  }

  function reset() {
    amount.value = null;
    goldTola.value = 0;
    silverTola.value = 0;
    selectedNisab.value = "silver";
  }

  onMounted(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw) as {
        amount?: number;
        goldTola?: number;
        silverTola?: number;
        selectedNisab?: NisabType;
      };
      if (typeof parsed.amount === "number" && parsed.amount > 0) {
        amount.value = Math.floor(parsed.amount);
      }
      if (typeof parsed.goldTola === "number" && parsed.goldTola >= 0) {
        goldTola.value = Math.round(parsed.goldTola * 10) / 10;
      }
      if (typeof parsed.silverTola === "number" && parsed.silverTola >= 0) {
        silverTola.value = Math.round(parsed.silverTola * 10) / 10;
      }
      if (parsed.selectedNisab === "silver" || parsed.selectedNisab === "gold") {
        selectedNisab.value = parsed.selectedNisab;
      }
    } catch {
      // Ignore invalid localStorage payload.
    }
  });

  watch([amount, goldTola, silverTola, selectedNisab], () => {
    if (!process.client) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        amount: amount.value,
        goldTola: goldTola.value,
        silverTola: silverTola.value,
        selectedNisab: selectedNisab.value,
      }),
    );
  });

  return {
    amount,
    goldTola,
    silverTola,
    selectedNisab,
    formattedAmount,
    amountInWords,
    metalRates,
    goldValue,
    silverValue,
    totalAmount,
    effectiveAmount,
    currentNisab,
    formattedCurrentNisab,
    isEligible,
    zakatAmount,
    formattedZakat,
    setAmount,
    increment,
    decrement,
    setGoldTola,
    setSilverTola,
    toggleNisab,
    reset,
  };
}
